import { isGlyphKey, isIconKey } from '@kk/domain';
import { colors, Icon, Text, tint, Title2, WasabiButton } from '@kk/ui';
import { useMemo, useState, type ChangeEvent } from 'react';

import { useContentCan } from '@/auth/SessionProvider';
import { deck } from '@/copy/deck';
import { useCheckIcon, useErrorMessage, useIconLibrary, usePublishIcon, useRetireIcon, useUploadIcon } from '@/data/hooks';
import type { IconCheck, QuestIcon, QuestIconDrawing } from '@/types/wire';

const c = deck.o14;

/**
 * **O14 · THE ICON LIBRARY** (the Quest Content Engine board; CONTENT_ENGINE.md §6). Every icon, bundled and uploaded,
 * with where each is used; a selected icon's card; and ADD AN ICON — a file through the validator and clean-up, the four
 * places it will appear, a key, and Add to library. An upload is saved as a DRAFT (Evan, 2026-09-27): it reaches phones
 * only when a Publisher who did not upload it presses Publish here, so this page is also where drafts go live.
 */
export function IconLibraryScreen() {
  const library = useIconLibrary();
  const [filter, setFilter] = useState<'picker' | 'draft' | 'uploaded' | 'retired'>('picker');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const icons = library.data?.icons ?? [];
  const counts = {
    picker: icons.filter((i) => i.status === 'live').length,
    draft: icons.filter((i) => i.status === 'draft').length,
    uploaded: icons.filter((i) => i.source === 'uploaded').length,
    retired: icons.filter((i) => i.status === 'retired').length,
  };
  const shown = icons.filter(
    (i) =>
      (filter === 'picker'
        ? i.status === 'live'
        : filter === 'draft'
          ? i.status === 'draft'
          : filter === 'uploaded'
            ? i.source === 'uploaded'
            : i.status === 'retired') && i.key.includes(search.trim().toLowerCase()),
  );
  const picked = icons.find((i) => i.key === selected) ?? null;

  return (
    <div className="stack" data-testid="icon-library">
      <div className="page-header">
        <div className="page-header-text">
          <Title2 tone="ink">{c.title}</Title2>
          {library.data ? (
            <Text variant="bodySm" tone="muted" testID="icon-manifest">
              {c.manifest(library.data.manifestVersion, counts.picker)}
            </Text>
          ) : null}
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' }}>
        <div className="stack" style={{ flex: '1 1 560px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <input
              className="input"
              placeholder={c.search}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="icon-search"
            />
            {(
              [
                ['picker', c.inPicker(counts.picker)],
                ['draft', c.draftChip(counts.draft)],
                ['uploaded', c.uploadedChip(counts.uploaded)],
                ['retired', c.retiredChip(counts.retired)],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                className={`chip${filter === k ? ' active' : ''}`}
                onClick={() => setFilter(k)}
                data-testid={`icon-filter-${k}`}
              >
                {label}
              </button>
            ))}
          </div>
          {shown.length === 0 && library.data ? (
            <Text variant="bodySm" tone="muted" testID="icon-nothing">
              {c.nothing}
            </Text>
          ) : null}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(112px,1fr))', gap: 10 }}>
            {shown.map((i) => (
              <button
                key={i.key}
                type="button"
                onClick={() => setSelected(i.key)}
                data-testid={`icon-${i.key}`}
                style={{
                  borderRadius: 14,
                  border: `2px solid ${selected === i.key ? colors.ink : colors.quietBorder}`,
                  background: i.status === 'retired' ? tint.quietFill : colors.white,
                  padding: 10,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 4,
                  cursor: 'pointer',
                }}
              >
                <Glyph icon={i} size={36} color={i.status === 'retired' ? tint.quietInk : colors.ink} />
                <Text variant="bodySm" tone="ink">
                  {i.key}
                </Text>
                <Text variant="bodySm" tone="muted">
                  {i.usedBy.length ? c.used(i.usedBy.length) : c.unused}
                </Text>
                {i.status === 'draft' ? <Tag>{c.draftTag}</Tag> : i.status === 'retired' ? <Tag>{c.retiredTag}</Tag> : i.source === 'uploaded' ? <Tag>{c.uploadedTag}</Tag> : null}
              </button>
            ))}
          </div>
        </div>
        <div className="stack" style={{ flex: '0 1 380px', minWidth: 300 }}>
          {picked ? <Selected icon={picked} manifestVersion={library.data?.manifestVersion ?? 1} /> : null}
          <AddAnIcon taken={(key) => icons.some((i) => i.key === key) || isGlyphKey(key)} />
        </div>
      </div>
    </div>
  );
}

function Tag({ children }: { children: string }) {
  return (
    <span style={{ background: colors.neutral, borderRadius: 999, padding: '2px 8px' }}>
      <Text variant="label" tone="slate">
        {children}
      </Text>
    </span>
  );
}

/** An icon as the app draws it: bundled by `@kk/ui`, uploaded from its stored path data (never the file). */
export function Glyph({ icon, size, color }: { icon: Pick<QuestIcon, 'key' | 'source' | 'drawing'>; size: number; color: string }) {
  if (icon.source === 'bundled') return <Icon name={icon.key as never} size={size} color={color} />;
  return icon.drawing ? <Drawing drawing={icon.drawing} size={size} color={color} /> : null;
}

function Drawing({ drawing, size, color }: { drawing: QuestIconDrawing; size: number; color: string }) {
  const stroke = drawing.kind === 'stroke';
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      {drawing.paths.map((d, n) => (
        <path
          key={n}
          d={d}
          fill={stroke ? 'none' : color}
          stroke={stroke ? color : 'none'}
          strokeWidth={stroke ? 2 : 0}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}

/** The selected icon's card: what it is, where it is used, and Publish (a draft) or Retire (a live upload). */
function Selected({ icon, manifestVersion }: { icon: QuestIcon; manifestVersion: number }) {
  const canPublish = useContentCan('publish');
  const publish = usePublishIcon();
  const retire = useRetireIcon();
  const errorMessage = useErrorMessage();
  // the key just published: its line reads the manifest version the library refetched after the publish
  const [published, setPublished] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = (fn: () => Promise<unknown>) => {
    setError(null);
    setPublished(null);
    fn().catch((e: unknown) => setError(errorMessage(e)));
  };
  const kind =
    icon.status === 'draft' ? c.draft : icon.status === 'retired' ? c.retired : icon.source === 'bundled' ? c.bundled : c.uploaded;
  return (
    <div
      className="stack"
      data-testid="icon-selected"
      style={{ border: `2px solid ${colors.slate}`, borderRadius: 18, padding: 16, background: colors.white }}
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <div
          style={{
            width: 88,
            height: 88,
            borderRadius: 20,
            border: `2px solid ${colors.ink}`,
            background: colors.paleTeal,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Glyph icon={icon} size={60} color={colors.ink} />
        </div>
        <div className="stack-tight">
          <Title2 tone="ink">{icon.key}</Title2>
          <Text variant="bodySm" tone="muted" testID="icon-kind">
            {kind}
          </Text>
        </div>
      </div>
      <Text variant="bodySm" tone="muted">
        {c.whereUsed}
      </Text>
      {icon.usedBy.length ? (
        icon.usedBy.map((u) => (
          <Text key={`${u.id}-${u.where}`} variant="bodySm" tone="ink">
            {`${u.title} · ${u.where}`}
          </Text>
        ))
      ) : (
        <Text variant="bodySm" tone="muted">
          {c.notUsed}
        </Text>
      )}
      <Text variant="bodySm" tone="muted">
        {icon.usedBy.length ? c.renameUsed : c.renameFree}
      </Text>
      {canPublish && icon.source === 'uploaded' && icon.status === 'draft' ? (
        <WasabiButton
          label={c.publish}
          size="sm"
          fullWidth={false}
          testID="icon-publish"
          onPress={() => run(async () => {
            await publish.mutateAsync(icon.key);
            setPublished(icon.key);
          })}
        />
      ) : null}
      {canPublish && icon.source === 'uploaded' && icon.status === 'live' ? (
        <button type="button" className="link" data-testid="icon-retire" onClick={() => run(() => retire.mutateAsync(icon.key))}>
          {c.retire}
        </button>
      ) : null}
      {published ? (
        <Text variant="bodySm" tone="ink" testID="icon-note">
          {c.published(published, manifestVersion)}
        </Text>
      ) : null}
      {error ? (
        <Text variant="bodySm" tone="error" testID="icon-error">
          {error}
        </Text>
      ) : null}
    </div>
  );
}

/** ADD AN ICON: a file through the validator, the clean-up shown before saving, the four previews, a key, Add. */
function AddAnIcon({ taken }: { taken: (key: string) => boolean }) {
  const canUpload = useContentCan('icons');
  const check = useCheckIcon();
  const upload = useUploadIcon();
  const errorMessage = useErrorMessage();
  const [file, setFile] = useState<{ name: string; svg: string } | null>(null);
  const [cleaned, setCleaned] = useState(false);
  const [key, setKey] = useState('');
  const [saved, setSaved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const result: IconCheck | undefined = check.data;

  const choose = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const svg = await f.text();
    setFile({ name: f.name, svg });
    setCleaned(false);
    setSaved(null);
    setError(null);
    setKey(f.name.replace(/\.svg$/i, '').toLowerCase());
    check.mutate(svg);
  };
  const keyProblem = useMemo(() => {
    const k = key.trim();
    if (!k) return null;
    if (!isIconKey(k)) return c.keyHelp;
    if (taken(k)) return c.keyTaken(k);
    return null;
  }, [key, taken]);
  if (!canUpload) return null;
  const needsCleanUp = !!result?.ok && result.fixes.length > 0 && !cleaned;
  const ready = !!result?.ok && !needsCleanUp && !!key.trim() && !keyProblem;

  return (
    <div
      className="stack"
      data-testid="icon-add"
      style={{ border: `1.5px solid ${colors.quietBorder}`, borderRadius: 18, padding: 16, background: colors.white }}
    >
      <Text variant="bodySm" tone="muted">
        {c.addTitle}
      </Text>
      <Text variant="bodySm" tone="ink">
        {c.addIntro}
      </Text>
      <label className="dropzone" style={{ border: `2px dashed ${tint.quietInk}`, borderRadius: 14, padding: 16, cursor: 'pointer' }}>
        <Text variant="bodySm" tone="ink">
          {file ? file.name : c.choose}
        </Text>
        <input type="file" accept=".svg,image/svg+xml" onChange={choose} style={{ display: 'none' }} data-testid="icon-file" />
      </label>
      {result ? (
        <div className="stack-tight" data-testid="icon-check">
          {result.errors.map((f) => (
            <Text key={f.message} variant="bodySm" tone="error">
              {`✕ ${f.message}`}
            </Text>
          ))}
          {result.fixes.map((f) => (
            <Text key={f} variant="bodySm" tone="ink">
              {`${cleaned ? '✓' : '!'} ${f}`}
            </Text>
          ))}
          <Text variant="bodySm" tone="ink" testID="icon-verdict">
            {!result.ok ? c.redraw : needsCleanUp ? c.mechanical : c.meets(result.drawing?.paths.length ?? 0)}
          </Text>
          {result.kind === 'fill' && result.ok ? (
            <Text variant="bodySm" tone="muted">
              {c.picture}
            </Text>
          ) : null}
          {needsCleanUp ? (
            <button type="button" className="link" data-testid="icon-clean-up" onClick={() => setCleaned(true)}>
              {c.cleanUp}
            </button>
          ) : null}
          {cleaned ? (
            <Text variant="bodySm" tone="muted">
              {c.cleanedUp}
            </Text>
          ) : null}
        </div>
      ) : null}
      {result?.ok && result.drawing && !needsCleanUp ? <Previews drawing={result.drawing} /> : null}
      {result?.ok && !needsCleanUp ? (
        <div className="stack-tight">
          <Text variant="bodySm" tone="muted">
            {c.key}
          </Text>
          <input className="input" value={key} onChange={(e) => setKey(e.target.value)} data-testid="icon-key" />
          <Text variant="bodySm" tone={keyProblem ? 'error' : 'muted'} testID="icon-key-help">
            {keyProblem ?? c.keyHelp}
          </Text>
          <div className="actions">
            <WasabiButton
              label={c.add}
              size="sm"
              fullWidth={false}
              testID="icon-upload"
              disabled={!ready}
              onPress={() => {
                setError(null);
                upload
                  .mutateAsync({ key: key.trim(), svg: file!.svg })
                  .then((made) => {
                    setSaved(c.savedDraft(made.key));
                    setFile(null);
                    check.reset();
                  })
                  .catch((e: unknown) => setError(errorMessage(e)));
              }}
            />
            <button
              type="button"
              className="link"
              onClick={() => {
                setFile(null);
                check.reset();
              }}
            >
              {c.cancel}
            </button>
          </div>
        </div>
      ) : null}
      {saved ? (
        <Text variant="bodySm" tone="ink" testID="icon-saved">
          {saved}
        </Text>
      ) : null}
      {error ? (
        <Text variant="bodySm" tone="error" testID="icon-upload-error">
          {error}
        </Text>
      ) : null}
    </div>
  );
}

/** WHERE IT WILL APPEAR: a quiz tile at 38, a badge disc at 20 (white on magenta), the grey unearned state, enlarged. */
function Previews({ drawing }: { drawing: QuestIconDrawing }) {
  const box = (ground: string, border: string, size: number, radius: number) => ({
    width: size,
    height: size,
    borderRadius: radius,
    background: ground,
    border: `2px solid ${border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  });
  return (
    <div className="stack-tight" data-testid="icon-previews">
      <Text variant="bodySm" tone="muted">
        {c.whereAppear}
      </Text>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <figure style={{ margin: 0 }}>
          <div style={box(colors.paleTeal, colors.ink, 72, 20)}>
            <Drawing drawing={drawing} size={38} color={colors.ink} />
          </div>
          <Text variant="bodySm" tone="muted">
            {c.previews.tile}
          </Text>
        </figure>
        <figure style={{ margin: 0 }}>
          <div style={box(colors.magenta, colors.ink, 40, 999)}>
            <Drawing drawing={drawing} size={20} color={colors.white} />
          </div>
          <Text variant="bodySm" tone="muted">
            {c.previews.badge}
          </Text>
        </figure>
        <figure style={{ margin: 0 }} data-testid="icon-preview-unearned">
          <div style={box(tint.quietFill, tint.quietInk, 72, 20)}>
            <Drawing drawing={drawing} size={38} color={tint.quietInk} />
          </div>
          <Text variant="bodySm" tone="muted">
            {c.previews.unearned}
          </Text>
        </figure>
        <figure style={{ margin: 0 }}>
          <div style={{ ...box(colors.white, colors.quietBorder, 104, 20), borderWidth: 1.5 }}>
            <Drawing drawing={drawing} size={88} color={colors.ink} />
          </div>
          <Text variant="bodySm" tone="muted">
            {c.previews.enlarged}
          </Text>
        </figure>
      </div>
    </div>
  );
}
